import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const quotes = [
  { it: "Sbagliando s'impara",                      en: "We learn by making mistakes" },
  { it: "La pazienza è la virtù dei forti",          en: "Patience is the virtue of the strong" },
  { it: "Nulla è impossibile a chi vuole",           en: "Nothing is impossible for those who want it" },
  { it: "Meglio tardi che mai",                      en: "Better late than never" },
  { it: "Chi va piano va sano e va lontano",         en: "He who goes slowly goes safely and goes far" },
  { it: "Chi ben comincia è a metà dell'opera",      en: "A good start is half the work" },
  { it: "Ognuno è fabbro della propria fortuna",     en: "Everyone is the architect of their own fortune" },
  { it: "La calma è la virtù dei forti",             en: "Calmness is the virtue of the strong" },
  { it: "Volere è potere",                           en: "Where there's a will, there's a way" },
  { it: "Chi dorme non piglia pesci",                en: "Those who sleep don't catch fish" },
  { it: "Batti il ferro finché è caldo",             en: "Strike while the iron is hot" },
  { it: "Ogni lasciata è persa",                     en: "Every missed opportunity is lost" },
  { it: "Tutto è bene quel che finisce bene",        en: "All's well that ends well" },
  { it: "Da soli si va più veloci, ma insieme si va più lontano", en: "Alone we go faster, but together we go further" },
  { it: "Ogni fiore vuole il suo tempo",             en: "Every flower needs its time" },
  { it: "L'unione fa la forza",                      en: "Unity is strength" },
  { it: "A buon intenditore, poche parole",          en: "A word to the wise is enough" },
  { it: "Sii il cambiamento che vuoi vedere",        en: "Be the change you want to see" },
  { it: "Oggi è un buon giorno per imparare",        en: "Today is a good day to learn" },
];

// ─── Announcement Bar ─────────────────────────────────────────────────────────
// ─── Announcement Bar ─────────────────────────────────────────────────────────
export const AnnouncementBar = ({ session }) => {
  const [isMobile,   setIsMobile]   = useState(window.innerWidth < 768);
  const [accepting,  setAccepting]  = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    
    supabase
      .from('settings')
      .select('accepting_students')
      .eq('id', 'global')
      .single()
      .then(({ data, error }) => { 
        if (data) setAccepting(data.accepting_students); 
      });

    return () => window.removeEventListener('resize', handleResize);
  }, []);

    if (accepting === null) return null;  // ← add this
    if (session) return null;


  const handleClick = () => {
    if (accepting) {
      // 🟢 Open State: Scroll down to Calendly booking section
      const el = document.getElementById('booking');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      // 🔴 Closed State: Route to a separate waitlist page
      const el = document.getElementById('waitlist');
      if (el) el.scrollIntoView({ behavior: 'smooth' });

      // OR Alternative Option: If your waitlist form is a section on the same page, 
      // comment out the navigate line above and use an element ID instead:
      // const el = document.getElementById('waitlist-section');
      // if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div onClick={handleClick} style={announcementStyle} role="banner">
      {accepting ? (
        /* 🟢 OPEN STATE */
        isMobile ? (
          <>
            <div>Currently taking on new students </div>
            <div style={{ fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '3px' }}>
              Book your free trial today!
            </div>
          </>
        ) : (
          <>
            Currently taking on new students -{' '}
            <span style={{ fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '3px' }}>
              Book your free trial today!
            </span>
          </>
        )
      ) : (
        /* 🔴 CLOSED STATE */
        isMobile ? (
          <>
            <div>Currently fully booked </div>
            <div style={{ fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '3px' }}>
              Join my waitlist →
            </div>
          </>
        ) : (
          <>
            Currently fully booked -{' '}
            <span style={{ fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '3px' }}>
              Join my waitlist →
            </span>
          </>
        )
      )}
    </div>
  );
};

const NavSeenTracker = ({ id, onSeen, children }) => {
  const ref = useRef(null);
  useEffect(() => {
    let timer;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          timer = setTimeout(() => onSeen(id), 2500);
        } else {
          clearTimeout(timer);
        }
      },
      { threshold: 0.6 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, [id, onSeen]);
  return <div ref={ref}>{children}</div>;
};

// ─── Navbar ───────────────────────────────────────────────────────────────────
const Navbar = ({ styles, fixed = false, isMaestra = false }) => {
    const navigate = useNavigate();

  const [session,          setSession]          = useState(null);
  const [firstName,        setFirstName]        = useState('');
  const [isMobile,         setIsMobile]         = useState(window.innerWidth < 1024);
  const [isQuoteHovered,   setIsQuoteHovered]   = useState(false);
  const [isProfileHovered, setIsProfileHovered] = useState(false);
  const [formattedDate,    setFormattedDate]    = useState('');
  const [notification,     setNotification]     = useState({ show: false, message: '', type: 'success' });
const [studentNotifications, setStudentNotifications] = useState([]);
const [seenIds, setSeenIds] = useState(new Set());
const [notifDropdownOpen, setNotifDropdownOpen] = useState(false);
  const todayQuote = quotes[new Date().getDate() % quotes.length];

  useEffect(() => {
    const extractName = (user) => {
      if (user?.user_metadata?.full_name) {
        setFirstName(user.user_metadata.full_name.split(' ')[0].toUpperCase());
      } else if (user?.email) {
        setFirstName(user.email.split('@')[0].toUpperCase());
      } else {
        setFirstName('S');
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      extractName(session?.user);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      extractName(session?.user);
    });

    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);

    setFormattedDate(
      new Intl.DateTimeFormat('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })
        .format(new Date())
    );

    return () => {
      window.removeEventListener('resize', handleResize);
      subscription.unsubscribe();
    };
  }, []);

      useEffect(() => {
  const loadNotifications = async () => {
    if (!session?.user?.id) {
      setStudentNotifications([]);
      setSeenIds(new Set());
      return;
    }
    const { data: notifs } = await supabase.from('notifications')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false });
    const relevant = (notifs || []).filter(n =>
      n.message?.startsWith('[REFUSED]') ||
      n.message?.startsWith('[CONFIRMED]') ||
      n.message?.startsWith('[MATERIAL]') ||
      n.message?.startsWith('[LEVEL_COMPLETE]') ||
      n.message?.startsWith('[TOPIC_COMPLETE]')
    );
    setStudentNotifications(relevant);
    setSeenIds(new Set(relevant.filter(n => n.is_read).map(n => n.id)));
  };
  loadNotifications();
}, [session?.user?.id]);

  // ── Keyboard: close dropdown on Escape ───────────────────────────────────
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsProfileHovered(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 7000);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const handlePasswordReset = async () => {
    showNotification('Un momento... processing your request.', 'process');
    const { error } = await supabase.auth.resetPasswordForEmail(session?.user?.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) {
      showNotification('Mi dispiace! Something went wrong. Per favore, try again.', 'error');
    } else {
      showNotification('Perfetto! Check your email for your secure link. A presto!', 'success');
      setIsProfileHovered(false);
    }
  };

  const handleNotificationSeen = useCallback(async (id) => {
  setSeenIds(prev => {
    if (prev.has(id)) return prev;
    const next = new Set(prev);
    next.add(id);
    return next;
  });
  const { error } = await supabase.from('notifications').update({ is_read: true }).eq('id', id);
  if (error) console.error('Failed to mark notification as read:', error.message);
}, []);

const unreadNotifCount = studentNotifications.filter(n => !seenIds.has(n.id)).length;

  const safeExternalStyles = styles?.nav || {};

  return (
    <>
    <style>{`
      @keyframes navBellPulse {
        0%, 100% { box-shadow: 0 0 0 0px rgba(193,68,14,0.35); }
        50%      { box-shadow: 0 0 0 6px rgba(193,68,14,0); }
      }
    `}</style>
    {/* ── Notification overlay ── */}
    {notification.show && (
      <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="Notification">
        <div style={{
          ...notificationCardStyle,
          borderLeft: `5px solid ${notification.type === 'error' ? '#B23A3A' : '#283618'}`,
        }}>
          <div style={iconStyle} aria-hidden="true">{notification.type === 'error' ? '⚠️' : '✉️'}</div>
          <div style={textContainerStyle}>
            <span style={labelStyle}>MESSAGE FROM FRANCESCA</span>
            <p style={messageStyle}>{notification.message}</p>
          </div>
          <button
            onClick={() => setNotification(n => ({ ...n, show: false }))}
            style={closeButtonStyle}
            aria-label="Close notification"
          >×</button>
        </div>
      </div>
    )}

    <div style={{
      position: fixed ? 'fixed' : 'relative',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 10000,
      width: '100%',
    }}>
      <AnnouncementBar session={session} />

      {session && isMobile && window.location.pathname !== '/portal' && (
        <button
          onClick={() => navigate('/portal?reset=1')}
          style={backBarStyle}
          aria-label="Back to your student dashboard"
        >
          <span aria-hidden="true" style={{ marginRight: '6px' }}>←</span>
          Back to your dashboard
        </button>
      )}

<nav
  style={{
    ...baseNavStyle,
    ...safeExternalStyles,
    padding: isMobile ? '10px 16px' : '12px 32px',
    backgroundColor: PAPER,
    borderBottom: '1px solid rgba(40, 54, 24, 0.05)',
    overflow: 'visible',
    gap: isMobile ? '12px' : 0,
  }}
  aria-label="Main navigation"
>

        {/* LEFT: Logo */}
        <div style={sectionStyle}>
          {/* ── CHANGED: refined editorial logo ── */}
          <Link to="/" style={logoLinkStyle} aria-label="SayCiao.school — home" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>          <svg width={isMobile ? "26" : "34"} height={isMobile ? "26" : "34"} viewBox="0 0 64 64" fill="none" style={{ flexShrink: 0 }}>              <rect width="64" height="64" rx="14" fill="#F7F4EF" stroke="rgba(0,0,0,0.04)" strokeWidth="1"/>
              <clipPath id="flag-nav"><rect width="64" height="64" rx="14"/></clipPath>
              <rect x="0"    y="0" width="21.3" height="64" fill="#009246" opacity="0.18" clipPath="url(#flag-nav)"/>
              <rect x="42.6" y="0" width="21.4" height="64" fill="#CE2B37" opacity="0.28" clipPath="url(#flag-nav)"/>
              <rect x="10" y="32" width="6" height="6"  rx="3" fill="#BC6C25" opacity="0.45"/>
              <rect x="19" y="26" width="6" height="18" rx="3" fill="#BC6C25" opacity="0.7"/>
              <rect x="28" y="14" width="6" height="42" rx="3" fill="#BC6C25"/>
              <rect x="37" y="20" width="6" height="30" rx="3" fill="#BC6C25" opacity="0.7"/>
              <rect x="46" y="28" width="6" height="14" rx="3" fill="#BC6C25" opacity="0.45"/>
            </svg>
<span style={logoWordmarkStyle}>
  <span style={getLogoSayStyle(isMobile)}>say</span>
  <span style={getLogoCiaoStyle(isMobile)}>ciao</span>
  {!isMobile && <span style={logoDotStyle}>.</span>}
  {!isMobile && <span style={logoSchoolStyle}>school</span>}
</span>
          </Link>
        </div>

        {/* MIDDLE: Daily quote */}
        {!isMobile && (
          <div
            style={centerSectionStyle}
            onMouseEnter={() => setIsQuoteHovered(true)}
            onMouseLeave={() => setIsQuoteHovered(false)}
            aria-label={`Italian proverb: ${isQuoteHovered ? todayQuote.en : todayQuote.it}`}
          >
            <span style={dateLabelStyle}>{formattedDate.toUpperCase()}</span>
            <span style={quoteValueStyle}>
              {isQuoteHovered ? `"${todayQuote.en}"` : `"${todayQuote.it}"`}
            </span>
            <div style={{ ...underlineStyle, width: isQuoteHovered ? '40px' : '20px' }} aria-hidden="true" />
          </div>
        )}

        {/* RIGHT: Auth actions */}
        <div style={rightSectionStyle}>
          {!session ? (
            <>

<Link
  to="/portal"
  style={{
    ...portalBtnStyle,
    ...(isMobile ? { padding: '7px 12px', fontSize: '0.65rem', letterSpacing: '1px' } : {}),
  }}
>
  {isMobile ? 'PORTAL' : 'STUDENT PORTAL'}
</Link>            </>
          ) : (
            <div style={profileContainerStyle}>
              {/* Book a Lesson — desktop only, not maestra */}
            {session && !isMaestra && (
  <div style={{ position: 'relative' }}>
    <button
      onClick={() => setNotifDropdownOpen(v => !v)}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center' }}
      aria-label="Open notifications"
      aria-expanded={notifDropdownOpen}
      aria-haspopup="true"
    >

<div style={{
        width: '38px', height: '38px', borderRadius: '50%',
        backgroundColor: unreadNotifCount > 0 ? '#fff5f0' : PAPER,
        border: `1.5px solid ${unreadNotifCount > 0 ? '#C1440E' : 'rgba(188,108,37,0.25)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: unreadNotifCount > 0 ? 'navBellPulse 2s infinite' : 'none',
      }}>
<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="4.5" width="20" height="15" rx="2" stroke={unreadNotifCount > 0 ? '#C1440E' : TERRACOTTA} strokeWidth="1.4" fill="none" />
    <path d="M2.5 5.5L12 13.5L21.5 5.5" stroke={unreadNotifCount > 0 ? '#C1440E' : TERRACOTTA} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
</div>

 {unreadNotifCount > 0 && (
  <span style={{
    position: 'absolute', top: '-6px', right: '-6px',
    minWidth: '20px', height: '20px', borderRadius: '10px',
    backgroundColor: '#C1440E', color: '#fff',
    fontSize: '0.68rem', fontWeight: 800,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '0 5px', fontFamily: "'Montserrat', sans-serif",
    border: '2px solid #fff',
    boxShadow: '0 2px 6px rgba(193,68,14,0.4)',
  }}>
    {unreadNotifCount}
  </span>
)}
    </button>

    {notifDropdownOpen && (
      <>
        <div onClick={() => setNotifDropdownOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 99998 }} aria-hidden="true" />
<div style={isMobile ? {
  position: 'fixed', top: '70px', left: '12px', right: '12px',
  maxHeight: '360px', overflowY: 'auto', backgroundColor: '#fff', borderRadius: '14px',
  border: '1px solid rgba(188,108,37,0.15)', boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
  padding: '14px', zIndex: 99999,
} : {
  position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: '320px', maxWidth: '90vw',
  maxHeight: '360px', overflowY: 'auto', backgroundColor: '#fff', borderRadius: '14px',
  border: '1px solid rgba(188,108,37,0.15)', boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
  padding: '14px', zIndex: 99999,
}}>            {studentNotifications.length === 0 ? (
            <p style={{ textAlign: 'center', fontSize: '0.78rem', color: '#999', padding: '20px 0', fontStyle: 'italic' }}>Nothing here yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {studentNotifications.map((n) => {
                const isConfirmed = n.message?.startsWith('[CONFIRMED]');
                const isMaterial  = n.message?.startsWith('[MATERIAL]');
                const isRefused   = n.message?.startsWith('[REFUSED]');
                const isLevelComplete = n.message?.startsWith('[LEVEL_COMPLETE]');
                const isTopicComplete = n.message?.startsWith('[TOPIC_COMPLETE]');
                const text = n.message.replace(/\[(REFUSED|CONFIRMED|MATERIAL|LEVEL_COMPLETE|TOPIC_COMPLETE)\]\s?/, '').trim();
                const config = isLevelComplete ? {
                  icon: '🏆', label: 'Level complete!', bg: '#fffbf0', border: 'rgba(188,108,37,0.3)', iconBg: '#fdefc3', labelColor: '#b8860b', textColor: '#3d4a2e',
                } : isTopicComplete ? {
                  icon: '📗', label: 'Topic mastered', bg: '#f7faf3', border: 'rgba(96,108,56,0.18)', iconBg: '#e8f0de', labelColor: '#606C38', textColor: '#3d4a2e',
                } : isConfirmed ? {
                  icon: '✅', label: 'Lesson confirmed', bg: '#f0f7ec', border: 'rgba(96,108,56,0.3)', iconBg: '#d4edda', labelColor: '#3a5a2a', textColor: '#3d4a2e',
                } : isMaterial ? {
                  icon: '📚', label: 'New resource available', bg: '#f0f4ff', border: 'rgba(88,108,196,0.25)', iconBg: '#dde3f8', labelColor: '#3a4a8a', textColor: '#3d4a2e',
                } : isRefused ? {
                  icon: '📬', label: 'From Francesca', bg: '#fff5f5', border: 'rgba(217,83,79,0.25)', iconBg: '#fde8e8', labelColor: '#a03030', textColor: '#3d4a2e',
                } : {
                  icon: '📬', label: 'From Francesca', bg: '#fff8f0', border: 'rgba(188,108,37,0.25)', iconBg: '#fde8cc', labelColor: TERRACOTTA, textColor: '#3d4a2e',
                };

                return (
                  <NavSeenTracker key={n.id} id={n.id} onSeen={handleNotificationSeen}>
 
<div
  onClick={() => {
    const targetTab = isMaterial ? 'resources'
      : (isTopicComplete || isLevelComplete) ? 'roadmap'
      : 'dashboard';
    setNotifDropdownOpen(false);
    const lessonParam = isConfirmed && n.lesson_id ? `&lessonId=${n.lesson_id}` : '';
    navigate(`/portal?tab=${targetTab}${lessonParam}`);
  }}
  style={{
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    padding: '12px 14px', borderRadius: '12px',
    backgroundColor: seenIds.has(n.id) ? '#fafafa' : config.bg,
    border: `1px solid ${seenIds.has(n.id) ? '#e0e0e0' : config.border}`,
    cursor: 'pointer',
  }}
>

                      <div style={{ width: '30px', height: '30px', borderRadius: '8px', backgroundColor: config.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', flexShrink: 0 }}>
                        {config.icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: '0 0 3px', fontSize: '0.72rem', fontWeight: 700, color: seenIds.has(n.id) ? '#aaa' : config.labelColor, display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                          <span>{config.label}</span>
                          <span style={{ fontSize: '0.6rem', fontWeight: 600, color: '#bbb', whiteSpace: 'nowrap' }}>
                            {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </p>
<p style={{
  margin: 0,
  fontSize: '0.72rem',
  color: config.textColor,
  lineHeight: '1.4',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}}>{text}</p>

           </div>
                    </div>
                  </NavSeenTracker>
                );
              })}
            </div>
          )}
        </div>
      </>
    )}
  </div>
)}

              {/* Avatar — tap to toggle dropdown */}
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setIsProfileHovered(v => !v)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  aria-label="Open profile menu"
                  aria-expanded={isProfileHovered}
                  aria-haspopup="true"
                >
<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={initialCircleStyle} aria-hidden="true">{firstName.charAt(0)}</div>
                    {!isMobile && <span style={{ fontSize: '0.65rem', fontWeight: 700, color: DEEP_MOSS, letterSpacing: '1px', fontFamily: "'Montserrat', sans-serif" }}>{firstName} ▾</span>}
                  </div>
                                  </button>

                {/* Dropdown */}
                {isProfileHovered && (
                  <>
                    <div
                      onClick={() => setIsProfileHovered(false)}
                      style={{ position: 'fixed', inset: 0, zIndex: 99998 }}
                      aria-hidden="true"
                    />
                    <div
                      style={{ ...dropdownStyle, ...(isMobile ? { right: '-8px', minWidth: '160px' } : {}) }}
                      role="menu"
                    >
                      {/* Book a Lesson — mobile dropdown only, not maestra */}

                     {!isMobile && (
                        <button
                          role="menuitem"
                          onClick={() => {
                            setIsProfileHovered(false);
                            navigate('/portal?reset=1');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          style={{ ...dropdownBaseItem, textDecoration: 'none', display: 'block', borderBottom: '1px solid rgba(40, 54, 24, 0.05)', color: TERRACOTTA, boxSizing: 'border-box' }}
                        >
                        🏠 My Dashboard

                        </button>
                      )}
                     <button role="menuitem" onClick={() => { setIsProfileHovered(false); handleLogout(); }} style={dropdownItemStyle}>🚪 Log Out</button>
<button role="menuitem" onClick={handlePasswordReset} style={dropdownSecondaryItemStyle}>🔑 Change Password</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

      </nav>
      </div>
    </>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const TERRACOTTA = '#BC6C25';
const DEEP_MOSS  = '#283618';
const PAPER      = '#FCF9F2';

const announcementStyle = {
  backgroundColor: '#B23A3A',
  color: PAPER,
  textAlign: 'center',
  padding: '9px 0',
  fontSize: '0.7rem',
  letterSpacing: '1px',
  textTransform: 'uppercase',
  fontFamily: "'Montserrat', sans-serif",
  fontWeight: 400,
  cursor: 'pointer',
  userSelect: 'none',
};

// ── CHANGED: slim back-to-dashboard bar ──────────────────────────────────────
const backBarStyle = {
  display: 'block',
  width: '100%',
  backgroundColor: DEEP_MOSS,
  color: PAPER,
  textAlign: 'center',
  padding: '9px 0',
  fontSize: '0.68rem',
  letterSpacing: '1.5px',
  textTransform: 'uppercase',
  fontFamily: "'Montserrat', sans-serif",
  fontWeight: 600,
  cursor: 'pointer',
  border: 'none',
  boxSizing: 'border-box',
};

const baseNavStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  zIndex: 10000,
  width: '100%',
  boxSizing: 'border-box',
};


const sectionStyle = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  overflow: 'visible',
  position: 'relative',
  zIndex: 10002,
};

const rightSectionStyle = {
  ...sectionStyle,
  justifyContent: 'flex-end',
  gap: '20px',
};

const centerSectionStyle = {
  flex: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  cursor: 'default',
};

// ── CHANGED: refined editorial logo ──────────────────────────────────────────
const logoLinkStyle   = { display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' };
const logoWordmarkStyle = { display: 'flex', alignItems: 'baseline', gap: 0 };
const getLogoSayStyle  = (isMobile) => ({ fontFamily: "'Montserrat', sans-serif", fontSize: isMobile ? '0.85rem' : '1rem', fontWeight: 600, letterSpacing: '1px', color: DEEP_MOSS });
const getLogoCiaoStyle = (isMobile) => ({ fontFamily: "'Montserrat', sans-serif", fontSize: isMobile ? '0.85rem' : '1rem', fontWeight: 600, letterSpacing: '1px', color: TERRACOTTA });
const logoDotStyle    = { fontFamily: "'Montserrat', sans-serif", fontSize: '1rem', fontWeight: 300, color: TERRACOTTA, opacity: 0.5 };
const logoSchoolStyle = { fontFamily: "'Montserrat', sans-serif", fontSize: '1rem', fontWeight: 300, letterSpacing: '1px', color: DEEP_MOSS, opacity: 0.85 };

// Quote
const dateLabelStyle  = { fontSize: '0.7rem', letterSpacing: '2px', fontWeight: 700, fontFamily: "'Montserrat', sans-serif", marginBottom: '4px', color: TERRACOTTA };
const quoteValueStyle = { fontSize: '0.85rem', fontWeight: 400, fontStyle: 'italic', fontFamily: "'Montserrat', sans-serif", transition: 'all 0.3s ease', minHeight: '1.2rem', color: DEEP_MOSS };
const underlineStyle  = { height: '1px', backgroundColor: TERRACOTTA, marginTop: '4px', opacity: 0.7, transition: 'width 0.3s ease' };

const portalBtnStyle = {
  fontSize: '0.72rem',
  letterSpacing: '2px',
  textTransform: 'uppercase',
  textDecoration: 'none',
  color: '#fff',
  backgroundColor: TERRACOTTA,
  padding: '8px 16px',
  borderRadius: '4px',
  fontFamily: "'Montserrat', sans-serif",
  fontWeight: 600,
};

// Profile
const profileContainerStyle = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '20px',
  height: '100%',
  padding: '10px 0',
  cursor: 'pointer',
};

const initialCircleStyle = {
  width: '38px',
  height: '38px',
  borderRadius: '50%',
  backgroundColor: TERRACOTTA,
  color: PAPER,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1rem',
  fontWeight: 600,
  fontFamily: "'Montserrat', sans-serif",
  boxShadow: '0 4px 12px rgba(188, 108, 37, 0.15)',
  cursor: 'pointer',
  border: 'none',
};

// Dropdown
const dropdownStyle = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  backgroundColor: PAPER,
  padding: '10px 0',
  borderRadius: '8px',
  border: '1px solid rgba(40, 54, 24, 0.1)',
  boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  display: 'flex',
  flexDirection: 'column',
  minWidth: '180px',
  zIndex: 99999,
};

const dropdownBaseItem = {
  background: 'none',
  border: 'none',
  fontSize: '0.72rem',
  letterSpacing: '1px',
  cursor: 'pointer',
  fontFamily: "'Montserrat', sans-serif",
  textTransform: 'uppercase',
  padding: '12px 20px',
  textAlign: 'center',
  fontWeight: 600,
  width: '100%',
};

const dropdownItemStyle = {
  ...dropdownBaseItem,
  color: DEEP_MOSS,
};

const dropdownSecondaryItemStyle = {
  ...dropdownBaseItem,
  borderTop: '1px solid rgba(40, 54, 24, 0.05)',
  color: '#555',
  fontSize: '0.72rem',
};

// Notification
const overlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(40, 54, 24, 0.3)',
  backdropFilter: 'blur(5px)',
  zIndex: 200000,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const notificationCardStyle = {
  backgroundColor: PAPER,
  padding: '25px 40px',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
  maxWidth: '90%',
  position: 'relative',
};

const iconStyle = {
  fontSize: '2rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(188, 108, 37, 0.1)',
  borderRadius: '50%',
  width: '50px',
  height: '50px',
};

const textContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  flex: 1,
};

const labelStyle = {
  fontSize: '0.55rem',
  letterSpacing: '2px',
  color: TERRACOTTA,
  fontWeight: 700,
  display: 'block',
  marginBottom: '5px',
};

const messageStyle = {
  margin: 0,
  fontFamily: "'Montserrat', sans-serif",
  color: DEEP_MOSS,
  fontSize: '0.9rem',
  lineHeight: '1.4',
};

const closeButtonStyle = {
  position: 'absolute',
  top: '10px',
  right: '15px',
  background: 'none',
  border: 'none',
  fontSize: '1.2rem',
  cursor: 'pointer',
  color: '#555',
};

export default Navbar;